import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, X, ExternalLink, RotateCcw } from 'lucide-react';
import { Document, Page } from 'react-pdf';
import * as pdfjs from 'pdfjs-dist';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const ProjectCarousel = ({ 
  pdfPath, 
  projectTitle, 
  uiStyle = 'inline', 
  onOpenFullPdf,
  showAllPages = false,
  maxPages = 3 
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log(`PDF loaded successfully: ${pdfPath}, pages: ${numPages}`);
    setNumPages(showAllPages ? numPages : Math.min(numPages, maxPages));
  };

  const onDocumentLoadError = (error) => {
    console.error(`PDF load error for ${pdfPath}:`, error);
  };

  const nextPage = () => {
    setCurrentPage(prev => prev < numPages ? prev + 1 : 1);
  };

  const prevPage = () => {
    setCurrentPage(prev => prev > 1 ? prev - 1 : numPages);
  };

  const goToPage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  // Auto-advance on hover for hover style
  useEffect(() => {
    if (uiStyle === 'hover' && isHovering && numPages > 1) {
      const interval = setInterval(nextPage, 2000);
      return () => clearInterval(interval);
    }
  }, [isHovering, numPages, uiStyle]);

  // Watermark component
  const Watermark = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 20 }, (_, i) => (
          <div
            key={i}
            className="absolute text-purple-600 font-bold text-lg transform -rotate-45"
            style={{
              left: `${(i * 15) % 100}%`,
              top: `${(i * 10) % 100}%`,
              animation: `watermarkFloat ${3 + (i % 3)}s ease-in-out infinite`
            }}
          >
            HyperHumane
          </div>
        ))}
      </div>
    </div>
  );

  // Navigation arrows
  const NavigationArrows = ({ className = "" }) => (
    <>
      <button
        onClick={prevPage}
        className={`absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-primary-700 p-2 rounded-full shadow-lg transition-all duration-200 z-10 ${className}`}
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={nextPage}
        className={`absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-primary-700 p-2 rounded-full shadow-lg transition-all duration-200 z-10 ${className}`}
      >
        <ChevronRight size={20} />
      </button>
    </>
  );

  // Page indicator
  const PageIndicator = ({ className = "" }) => (
    <div className={`flex justify-center space-x-2 mt-4 ${className}`}>
      {Array.from(new Array(numPages), (_, index) => (
        <button
          key={index + 1}
          onClick={() => goToPage(index + 1)}
          className={`w-2 h-2 rounded-full transition-all duration-200 ${
            currentPage === index + 1 ? 'bg-primary-600' : 'bg-primary-300'
          }`}
        />
      ))}
    </div>
  );

  // Thumbnail strip with navigation arrows
  const ThumbnailStrip = () => {
    const [startIndex, setStartIndex] = useState(0);
    const maxVisible = 5; // Show max 5 thumbnails at once
    
    // Update startIndex when currentPage changes to keep current page centered
    useEffect(() => {
      const targetIndex = Math.max(0, currentPage - Math.floor(maxVisible / 2) - 1);
      const maxStartIndex = Math.max(0, numPages - maxVisible);
      setStartIndex(Math.min(targetIndex, maxStartIndex));
    }, [currentPage, numPages, maxVisible]);
    
    const nextThumbnails = () => {
      setStartIndex(prev => Math.min(prev + 1, numPages - maxVisible));
    };
    
    const prevThumbnails = () => {
      setStartIndex(prev => Math.max(prev - 1, 0));
    };
    
    return (
      <div className="relative w-full mt-4">
        {/* Navigation arrows */}
        {numPages > maxVisible && startIndex > 0 && (
          <button
            onClick={prevThumbnails}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-1 rounded-full shadow-lg transition-all duration-200 z-10"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        
        {numPages > maxVisible && startIndex < numPages - maxVisible && (
          <button
            onClick={nextThumbnails}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-1 rounded-full shadow-lg transition-all duration-200 z-10"
          >
            <ChevronRight size={16} />
          </button>
        )}
        
        {/* Thumbnail container with overflow hidden */}
        <div className="flex justify-center space-x-2 overflow-hidden px-6">
          {Array.from(new Array(numPages), (_, index) => {
            if (index < startIndex || index >= startIndex + maxVisible) return null;
            
            return (
              <button
                key={index + 1}
                onClick={() => goToPage(index + 1)}
                className={`flex-shrink-0 relative ${
                  currentPage === index + 1 ? 'ring-2 ring-purple-600' : ''
                }`}
              >
                <Document 
                  file={pdfPath}
                  onLoadError={onDocumentLoadError}
                  loading={<div className="text-primary-600 text-xs">Loading...</div>}
                  error={<div className="text-red-600 text-xs">Error</div>}
                >
                  <Page
                    pageNumber={index + 1}
                    width={60}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Main PDF preview with navigation
  const MainPreview = () => (
    <div className="relative w-full bg-white rounded-lg overflow-hidden shadow-lg">
      {/* Navigation arrows for main preview */}
      {numPages > 1 && (
        <>
          <button
            onClick={() => goToPage(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-2 rounded-full shadow-lg transition-all duration-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => currentPage === numPages ? goToPage(1) : goToPage(Math.min(currentPage + 1, numPages))}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-purple-100 hover:bg-purple-200 text-purple-800 p-2 rounded-full shadow-lg transition-all duration-200 z-10"
          >
            {currentPage === numPages ? <RotateCcw size={20} /> : <ChevronRight size={20} />}
          </button>
        </>
      )}
      
      {/* Page indicator */}
      {numPages > 1 && (
        <div className="absolute top-2 right-2 bg-white/90 px-2 py-1 rounded text-xs text-purple-800 font-medium z-10">
          {currentPage} of {numPages}
        </div>
      )}
      
      <div className="flex justify-center w-full p-4 min-h-[400px]">
        <Document 
          file={pdfPath} 
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<div className="text-primary-600">Loading PDF...</div>}
          error={<div className="text-red-600">Failed to load PDF</div>}
        >
          <Page
            pageNumber={currentPage}
            width={500}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
        <Watermark />
      </div>
    </div>
  );

  // Render different UI styles
  const renderCarousel = () => {
    switch (uiStyle) {
      case 'inline':
        return (
          <div className="relative">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg">
              <Document 
                file={pdfPath} 
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-primary-600">Loading PDF...</div>}
                error={<div className="text-red-600">Failed to load PDF</div>}
              >
                <Page
                  pageNumber={currentPage}
                  width={400}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              <Watermark />
              {numPages > 1 && <NavigationArrows />}
            </div>
            {numPages > 1 && <PageIndicator />}
            <button
              onClick={onOpenFullPdf}
              className="mt-4 w-full bg-purple-100 text-purple-800 hover:bg-purple-200 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ExternalLink size={16} />
              Expand Presentation
            </button>
          </div>
        );

      case 'modal':
        return (
          <>
            <div 
              className="relative bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              <Document file={pdfPath} onLoadSuccess={onDocumentLoadSuccess}>
                <Page
                  pageNumber={1}
                  width={400}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              <Watermark />
              <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <span className="text-white font-medium">Click to view slides</span>
              </div>
            </div>

            <AnimatePresence>
              {isModalOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                  onClick={() => setIsModalOpen(false)}
                >
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-lg p-6 max-w-4xl max-h-[90vh] overflow-auto"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-purple-900">{projectTitle}</h3>
                      <button
                        onClick={() => setIsModalOpen(false)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <X size={24} />
                      </button>
                    </div>
                    
                    <div className="relative">
                      <Document 
                        file={pdfPath} 
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading={<div className="text-purple-600">Loading PDF...</div>}
                        error={<div className="text-red-600">Failed to load PDF</div>}
                      >
                        <Page
                          pageNumber={currentPage}
                          width={600}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                        />
                      </Document>
                      <Watermark />
                      {numPages > 1 && <NavigationArrows />}
                    </div>
                    
                    {numPages > 1 && <PageIndicator />}
                    
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={onOpenFullPdf}
                        className="bg-purple-100 text-purple-800 hover:bg-purple-200 px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <ExternalLink size={16} />
                        Expand Presentation
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        );

      case 'hover':
        return (
          <div 
            className="relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg">
              <Document 
                file={pdfPath} 
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-primary-600">Loading PDF...</div>}
                error={<div className="text-red-600">Failed to load PDF</div>}
              >
                <Page
                  pageNumber={currentPage}
                  width={400}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              <Watermark />
              {isHovering && numPages > 1 && <NavigationArrows />}
            </div>
            <button
              onClick={onOpenFullPdf}
              className="mt-4 w-full bg-purple-100 text-purple-800 hover:bg-purple-200 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ExternalLink size={16} />
              Expand Presentation
            </button>
          </div>
        );

      case 'thumbnail':
        return (
          <div className="w-full">
            <MainPreview />
            <ThumbnailStrip />
            {onOpenFullPdf && (
              <button
                onClick={onOpenFullPdf}
                className="mt-4 w-full bg-purple-100 text-purple-800 hover:bg-purple-200 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink size={16} />
                Expand Presentation
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {renderCarousel()}
      
      {/* Watermark animation CSS */}
      <style jsx>{`
        @keyframes watermarkFloat {
          0%, 100% { opacity: 0.1; transform: translateY(0px) rotate(-45deg); }
          50% { opacity: 0.2; transform: translateY(-2px) rotate(-45deg); }
        }
      `}</style>
    </div>
  );
};

export default ProjectCarousel; 